import { ICON_BASE_PATH } from "@/app/utils/config";
import { TextBox, type TextBoxChangeEvent } from "@progress/kendo-react-inputs";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";

interface BeneficiaryAccountProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onValidate?: (field: string, isValid: boolean) => void;
  validationMessage?: string;
  showStarLabel?: boolean;
  disable?: boolean;
  ibanRequired?: boolean;
  ibanLength?: number | null;
  ibanStatus?: "success" | "error" | null;
  onBlur?: () => void;
  iconSrc?: string;
  triggerValidation?: boolean;
  ibanError?: string;
  ibanSuccess?: string;
}

const BeneficiaryAccount: React.FC<BeneficiaryAccountProps> = ({
  label,
  value,
  onChange,
  onValidate,
  validationMessage,
  showStarLabel = true,
  disable = false,
  ibanRequired = false,
  ibanLength = null,
  ibanStatus = null,
  onBlur,
  iconSrc = "beneficiaryaccount.svg",
  triggerValidation = false,
  ibanError,
  ibanSuccess,
}) => {
  const { t } = useTranslation();

  const inputId = useId();
  const errorId = `${inputId}-error`;
  const successId = `${inputId}-success`;

  const [touched, setTouched] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e: TextBoxChangeEvent) => {
    const raw = String(e.value ?? "");
    let filtered = raw;

    if (ibanRequired) {
      filtered = raw.toUpperCase().replace(/[^A-Z0-9]/g, "");

      if (ibanLength && filtered.length > ibanLength) {
        filtered = filtered.slice(0, ibanLength);
      }
    } else {
      filtered = raw.replace(/[^0-9]/g, "").slice(0, 30);
    }

    onChange(filtered);
  };

  const validate = (val: string): string => {
    if (!val) {
      return "Please enter the account/IBAN number.";
    }

    if (ibanRequired) {
      if (!/^[A-Z]{2}/.test(val)) {
        return "IBAN must start with 2 letters followed by digits.";
      }

      if (!/^[A-Z0-9]+$/.test(val)) {
        return "Invalid IBAN format.";
      }

      if (ibanLength && val.length !== ibanLength) {
        return `IBAN must be ${ibanLength} characters.`;
      }

      if (val.length < 15 || val.length > 34) {
        return "IBAN must be between 15 and 34 characters.";
      }
    } else {
      if (!/^[0-9]+$/.test(val)) {
        return "Account number must contain digits only.";
      }

      if (val.length < 9) {
        return "Beneficiary Account/IBAN Number must be at least 9 characters.";
      }
    }

    return "";
  };

  const runValidation = (val: string) => {
    const msg = validate(val);
    setError(msg);
    onValidate?.(label, msg === "");
  };

  const handleBlur = () => {
    setTouched(true);
    setIsFocused(false);
    runValidation(value);
    onBlur?.();
  };

  useEffect(() => {
    if (triggerValidation) {
      setTouched(true);
      runValidation(value);
    }
  }, [triggerValidation]);

  useEffect(() => {
    if (validationMessage) {
      setError(validationMessage);
      onValidate?.(label, false);
    }
  }, [validationMessage]);

  const hasValue = value.trim().length > 0;

  const showSuccessIcon = hasValue && ibanStatus === "success";

  const showErrorIcon = hasValue && ibanStatus === "error";

  const isError = (!!error || !!validationMessage) && touched;

  const clearInput = () => {
    onChange("");
  };

  const handleClearKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      clearInput();
    }
  };

  return (
    <div className="gx_input_container">
      <label
        htmlFor={inputId}
        className={`input-label gx-font-semibold ${
          value || isFocused ? "floating" : ""
        }`}
      >
        {t(label)}

        {showStarLabel && (
          <span
            aria-hidden="true"
            className={`gx_input_star ${
              value ? "gx_input_star_float" : "gx_input_star_normal"
            }`}
          >
            *
          </span>
        )}

        {showStarLabel && <span className="sr-only">Required field</span>}
      </label>

      <div
        className={`gx_input_cont ${isError ? "invalid" : "valid"} ${
          disable ? "opacity-60 pointer-events-none" : ""
        }`}
      >
        <img src={`${ICON_BASE_PATH}${iconSrc}`} alt="" aria-hidden="true" />

        <TextBox
          id={inputId}
          value={value}
          onChange={handleChange}
          disabled={disable}
          className="gx-textbox-css"
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          aria-required={showStarLabel}
          aria-invalid={isError}
          aria-describedby={
            error ? errorId : ibanSuccess ? successId : undefined
          }
        />

        {showSuccessIcon && (
          <img
            src={`${ICON_BASE_PATH}check_verify.svg`}
            className="w-5 h-5 ml-2 shrink-0"
            alt="Account verified"
          />
        )}

        {showErrorIcon && (
          <button
            type="button"
            className="ml-2 shrink-0"
            onClick={clearInput}
            onKeyDown={handleClearKeyDown}
            aria-label="Clear invalid account number"
          >
            <img
              src={`${ICON_BASE_PATH}failed-icon.svg`}
              alt=""
              aria-hidden="true"
            />
          </button>
        )}
      </div>

      {touched && (error || ibanError) && (
        <div
          id={errorId}
          role="alert"
          aria-live="assertive"
          className="gx_input_validationmessage gx-font-regular text-[#FF0000]"
        >
          {error || ibanError}
        </div>
      )}

      {touched && !error && !ibanError && ibanSuccess && (
        <div
          id={successId}
          role="status"
          aria-live="polite"
          className="gx_input_validationmessage gx-font-regular text-green-600"
        >
          {ibanSuccess}
        </div>
      )}
    </div>
  );
};

export default BeneficiaryAccount;
